import React from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Plus, Trash2
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import FieldHelp from '../components/FieldHelp';

const IDENTIFIER_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Simple-mode typed inputs: name + description only (type defaults to string).
 */
export default function SimpleTypedInputsEditor({
    inputs, onChange, embedded = false
}) {
    const list = Array.isArray(inputs) ? inputs : [];

    function update(idx, patch) {
        const next = list.map((it, i) => (i === idx ? { ...it, ...patch } : it));
        onChange(next);
    }

    function add() {
        let i = list.length + 1;
        let name = `input_${i}`;
        while (list.some((x) => x.name === name)) name = `input_${++i}`;
        onChange([...list, {
            name,
            type: 'string',
            required: true,
            description: ''
        }]);
    }

    function remove(idx) {
        onChange(list.filter((_, i) => i !== idx));
    }

    const inputClass = embedded ? 'h-7 text-xs rounded-sm' : 'h-8 rounded-sm';
    const headClass = cn(
        'h-8 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground',
        embedded && 'h-7 text-[10px]'
    );
    const cellClass = 'px-2 py-1.5';

    return (
        <div className={cn('space-y-1.5', embedded && 'text-xs')}>
            {!embedded && (
                <FieldHelp>What information does this procedure need to run?</FieldHelp>
            )}
            {list.length === 0 && (
                <p className="text-xs text-muted-foreground leading-snug">
                    {embedded
                        ? 'None — optional.'
                        : 'No inputs — add one if this procedure needs data (e.g. question, order ID).'}
                </p>
            )}
            {list.length > 0 && (
                <div className="overflow-hidden rounded-sm border border-foreground/20">
                    <Table className="table-fixed w-full">
                        <colgroup>
                            {embedded ? (
                                <>
                                    <col />
                                    <col className="w-9" />
                                </>
                            ) : (
                                <>
                                    <col className="w-1/3" />
                                    <col className="w-2/3" />
                                    <col className="w-9" />
                                </>
                            )}
                        </colgroup>
                        <TableHeader>
                            <TableRow className="border-foreground/20 hover:bg-transparent">
                                <TableHead className={cn(headClass, !embedded && 'w-1/3')}>Name</TableHead>
                                {!embedded && (
                                    <TableHead className={cn(headClass, 'w-2/3')}>Description</TableHead>
                                )}
                                <TableHead className={cn(headClass, 'w-9 text-right')} />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {list.map((inp, idx) => (
                                <TableRow key={idx} className="border-foreground/15 hover:bg-muted/30">
                                    <TableCell className={cellClass}>
                                        <Input
                                            value={inp.name || ''}
                                            onChange={(e) => update(idx, { name: e.target.value })}
                                            placeholder="e.g. query"
                                            className={inputClass}
                                        />
                                    </TableCell>
                                    {!embedded && (
                                        <TableCell className={cellClass}>
                                            <Input
                                                value={inp.description || ''}
                                                onChange={(e) => update(idx, { description: e.target.value })}
                                                placeholder="Short description for teammates"
                                                className={inputClass}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell className={cn(cellClass, 'text-right')}>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                'text-destructive',
                                                embedded ? 'h-7 w-7' : 'h-8 w-8'
                                            )}
                                            onClick={() => remove(idx)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            <Button
                variant="secondary"
                size="sm"
                className={cn(
                    'border border-primary/35 bg-primary/15 font-semibold text-primary shadow-none hover:bg-primary/25',
                    embedded && 'h-7 text-xs'
                )}
                onClick={add}
            >
                <Plus className="mr-1 h-3 w-3" /> Add
            </Button>
        </div>
    );
}

export function validateSimpleInputs(inputs) {
    const errs = [];
    (inputs || []).forEach((inp, i) => {
        if (!inp.name?.trim()) errs.push(`Input ${i + 1}: name required`);
        else if (!IDENTIFIER_REGEX.test(inp.name)) errs.push(`Input ${i + 1}: invalid name`);
    });
    return errs;
}
